/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
import { Button, Checkbox, Icon } from '@pwc/pwc-deals-ui';
import { Constants, NavigationNodeId } from '@pwc/pwc-deals-web-common';
import { cloneDeep, get, keys, omit, xor } from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { getNavigationTitle } from 'common/helpers/getCustomNavigationTitles';
import ConnectedContentContext from '../../../features/narratives/Context/ConnectedContentContext';
import {
  changeConnectedContentPreference,
  getConnectedContentWarning,
} from '../../redux/userSettings/userSettingsSlice';
import ConnectContentModal from '../ConnectContentModal/ConnectContentModal';
import './ConnectedContentRow.scss';

const applyButtonId = 'apply-tag-button';
const createButtonId = 'create-tag-button';
const optId = (_strings, opt) => `opt-${opt.id}`;

const useBlurHandler = (ids, onFocusOutside) => (ev) => {
  const { relatedTarget } = ev;
  const focusedId = get(relatedTarget, 'id', '');
  const focusIsWithin = ids.some((id) => focusedId === id);

  if (relatedTarget && !focusIsWithin && onFocusOutside) {
    onFocusOutside();
  }
};

const ConnectedContentRow = ({
  projectId,
  parentContentId,
  options,
  onSubmit,
  connectedContentType,
  innerIdWhitelist = [],
  modalTitle,
  setCloseDropDown,
  modalCheckboxLabel,
  linkNarrative,
  connectedContent,
  isModalOpen,
}) => {
  const [currentlySelectedTags, setCurrentlySelectedTags] = useState({});

  const stateRef = useRef();
  const { t } = useTranslation();
  stateRef.current = currentlySelectedTags;
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const [modalCheckboxValue, setModalCheckboxValue] = useState(false);
  const [currentNarrative, setCurrentNarrative] = useState({});
  const [tagsChanged, setTagsChanged] = useState(false);
  const [contentWhatSelected, setContentWhatSelected] = useState([]);
  const titleLimit = 35;
  const { url } = useRouteMatch();

  const KFATitle = getNavigationTitle(NavigationNodeId.KEY_FOCUS_AREAS);

  const isAnaSharedAndAlreadyConnected = useMemo(
    () =>
      currentNarrative.versionStatus === 3 &&
      currentNarrative.links.filter((l) => l.type === 200).length > 0,
    [currentNarrative.links, currentNarrative.versionStatus]
  );
  const [keyTags, setKeyTags] = useState([]);

  const { previousConnections, setLastSelectedKFA } = useContext(
    ConnectedContentContext
  );

  useEffect(() => {
    if (connectedContentType === 'analyses') {
      const connectedContentNew = connectedContent.reduce((acc, cur) => {
        acc[cur.title] = true;
        return acc;
      }, {});
      setCurrentlySelectedTags(connectedContentNew);
    } else if (
      connectedContentType === 'hypotheses' &&
      previousConnections &&
      previousConnections.hypotheses.length
    ) {
      setCurrentlySelectedTags({
        [previousConnections.hypotheses[0].title]: true,
      });
    }
  }, [
    parentContentId,
    setCurrentlySelectedTags,
    connectedContentType,
    previousConnections,
    connectedContent,
  ]);

  const dispatch = useDispatch();

  const parentContentType = useMemo(() => {
    if (url.includes('key-focus-area')) {
      return Constants.ContentTypes.Hypothesis;
    }
    return Constants.ContentTypes.Analysis;
  }, [url]);

  const contentType = useMemo(
    () => (parentContentType === 200 ? 'hypotheses' : 'analyses'),
    [parentContentType]
  );
  const {
    [contentType]: { [projectId]: contentWarningSetting },
  } = useSelector(getConnectedContentWarning);

  const blurHandler = useBlurHandler([
    ...options.map((opt) => optId`${opt}`),
    ...innerIdWhitelist,
    applyButtonId,
    createButtonId,
  ]);

  const handleConnectedContentConfirm = () => {};
  const handleDismissModal = () => {
    const updatedTags = omit(currentlySelectedTags, [keyTags.pop()]);
    setCurrentlySelectedTags(updatedTags);
    setKeyTags([...keys(updatedTags)]);
    setShowModalConfirmation(false);
    isModalOpen(false);
  };

  const saveWarningPreference = useCallback(() => {
    if (modalCheckboxValue && contentWarningSetting) {
      const featureName =
        contentType === 'analyses'
          ? 'Analysis_Link_Warning'
          : 'KFA_Link_Warning';
      dispatch(
        changeConnectedContentPreference(
          projectId,
          contentType,
          featureName,
          !contentWarningSetting
        )
      );
    }
  }, [
    contentWarningSetting,
    modalCheckboxValue,
    projectId,
    contentType,
    dispatch,
  ]);

  const showWarningForKFA = useCallback(
    (narrative) => {
      const { links } = narrative;
      const alreadyConnectedToKFA = links.some(
        (l) => l.type === parentContentType
      );
      const previouslyConnectedIndex = previousConnections.analyses.findIndex(
        (_) => _.contentId === narrative.contentId
      );
      return alreadyConnectedToKFA && previouslyConnectedIndex < 0;
    },
    [parentContentType, previousConnections]
  );

  const showWarningForAnalysis = useCallback(
    (narrative) => {
      const hypothesesConnected =
        get(previousConnections, 'hypotheses', []).length > 0;
      const previouslyConnectedIndex = previousConnections.hypotheses.findIndex(
        (_) => _.contentId === narrative.contentId
      );
      return hypothesesConnected && previouslyConnectedIndex < 0;
    },
    [previousConnections]
  );

  const showWarning = useCallback(
    (narrative) => {
      let toShow = false;
      if (
        parentContentType === Constants.ContentTypes.Hypothesis &&
        narrative.type === Constants.ContentTypes.Analysis
      ) {
        toShow = showWarningForKFA(narrative);
      }
      if (
        parentContentType === Constants.ContentTypes.Analysis &&
        narrative.type === Constants.ContentTypes.Analysis
      ) {
        toShow = showWarningForAnalysis(narrative);
      }

      return toShow;
    },
    [parentContentType, showWarningForKFA, showWarningForAnalysis]
  );

  const link = useCallback(
    (selectedNarrative) => {
      const id = parseInt(parentContentId, 10);
      const editedNarrative = cloneDeep(selectedNarrative);
      const contentLink = editedNarrative.links.find((l) => l.id === id) || {
        id,
      };
      const filteredLinks = editedNarrative.links.filter((_) => _.id !== id);
      editedNarrative.links = [
        ...filteredLinks,
        { ...contentLink, linked: true },
      ];

      linkNarrative(editedNarrative, connectedContentType);

      if (editedNarrative.type === Constants.ContentTypes.Hypothesis) {
        setLastSelectedKFA(editedNarrative.contentId);
      }
    },
    [setLastSelectedKFA, parentContentId, linkNarrative, connectedContentType]
  );

  useEffect(() => {
    setTagsChanged(xor(keys(currentlySelectedTags)).length);
  }, [currentlySelectedTags]);

  const handleOnSubmit = useCallback(() => {
    contentWhatSelected.filter((content) => {
      if (keyTags.includes(content.title)) {
        link(content);
      } else {
        const existingLink = content.links.findIndex(
          (l) => l.id === parseInt(parentContentId, 10)
        );
        if (existingLink > -1) {
          content.links[existingLink].linked = false;
        }

        linkNarrative(content, connectedContentType);
      }

      return null;
    });

    onSubmit(keyTags);
    setCloseDropDown();
  }, [
    onSubmit,
    keyTags,
    parentContentId,
    link,
    contentWhatSelected,
    setCloseDropDown,
    connectedContentType,
    linkNarrative,
  ]);

  const handleOnSubmitKFA = useCallback(
    (contentKFA, tag) => {
      contentKFA.filter((content) => {
        if (tag.includes(content.title)) {
          link(content);
        }

        return null;
      });

      onSubmit([tag]);
      setCloseDropDown();
    },
    [onSubmit, link, setCloseDropDown]
  );

  const updateCheckedTags = (selectedContent) => {
    const selectedTag = selectedContent.title;
    const toShow = showWarning(selectedContent);

    const alreadyChecked = currentlySelectedTags[selectedTag];
    const isConnected = selectedContent.links.some(
      (l) => l.type === Constants.ContentTypes.Hypothesis
    );

    setContentWhatSelected((prevState) => [...prevState, selectedContent]);

    if (alreadyChecked) {
      const updatedTags = omit(currentlySelectedTags, [selectedTag]);
      setCurrentlySelectedTags(updatedTags);
      setKeyTags([...keys(updatedTags)]);
    } else {
      setCurrentlySelectedTags({
        ...currentlySelectedTags,
        [selectedTag]: true,
      });

      setKeyTags([
        ...keys({
          ...currentlySelectedTags,
          [selectedTag]: true,
        }),
      ]);

      const isShared =
        selectedContent.versionStatus === 3 &&
        selectedContent.links.filter((l) => l.type === 200).length > 0;
      if (isShared && parentContentType === Constants.ContentTypes.Hypothesis) {
        setShowModalConfirmation(true);
        isModalOpen(true);
        return;
      }
      if (
        isConnected &&
        parentContentType === Constants.ContentTypes.Hypothesis
      ) {
        if (toShow && contentWarningSetting) {
          setShowModalConfirmation(true);
          isModalOpen(true);
        }
      }
    }
  };

  const handleModalConfirm = () => {
    saveWarningPreference();
    setShowModalConfirmation(false);
    isModalOpen(false);
  };

  const updateKeyFocusAreas = useCallback(
    (selectedTag) => {
      setKeyTags([selectedTag.title]);
      handleOnSubmitKFA([selectedTag], selectedTag.title);
    },
    [handleOnSubmitKFA]
  );

  const handleCheckboxChange = useCallback(() => {
    setModalCheckboxValue((prev) => !prev);
  }, [setModalCheckboxValue]);

  const handleEscapeKey = (ev) => {
    if (ev.keyCode === 27) {
      setCloseDropDown();
    }
  };
  const modalBody = useMemo(() => {
    if (parentContentType === 100) {
      return 'It is possible to connect only one KFA for each analysis. Clicking on "connect" will replace the existing connection.';
    }

    if (isAnaSharedAndAlreadyConnected) {
      return `The shared analysis is connected to another ${KFATitle}. Please change the connection from the Analysis page.`;
    }

    return t('analysisConnectedAnotherKFATitle', { KFATitle });
  }, [parentContentType, isAnaSharedAndAlreadyConnected, KFATitle]); // eslint-disable-line

  const handleConnectedContent = (narrativeItem) => {
    const isConnected = narrativeItem.links.some((l) => l.type === 200);
    if (narrativeItem.links.length) {
      const isSelected = currentlySelectedTags[narrativeItem.title] === true;
      if (isSelected && isConnected) {
        return (
          <div
            className="connected-content-info-with-tag"
            data-testid="content-with-tag"
          >
            <Icon
              name="hyperlink"
              width={16}
              color="primary"
              className="fill icon-orange connect-content-icon"
            />
            {t('replaceConnection')}
          </div>
        );
      }
      if (isConnected) {
        return (
          <div
            className="connected-content-info"
            data-testid="connected-content-info"
          >
            <Icon
              name="hyperlink"
              width={16}
              color="primary"
              className="fill icon-grey connect-content-icon"
            />
            {t('contentConnectedAnotherKfaTitle', { KFATitle })}
          </div>
        );
      }
    }
    return null;
  };

  const truncate = (input) =>
    input && input.length > titleLimit
      ? `${input.substring(0, titleLimit)}...`
      : input;

  const isConnected = (narrativeItem) => {
    return narrativeItem.links.some((l) => l.type === 200);
  };

  return (
    <div
      className="connected-content-row-container"
      data-testid="connected-content-row-container"
    >
      <div data-keyboard="false" data-backdrop="static">
        <Modal
          show={showModalConfirmation}
          onHide={handleConnectedContentConfirm}
          centered
          backdrop="static"
        >
          <ConnectContentModal
            onModalConfirm={handleModalConfirm}
            onModalDismiss={handleDismissModal}
            modalTitle={
              isAnaSharedAndAlreadyConnected ? 'Unable to connect' : modalTitle
            }
            modalBody={modalBody}
            modalCheckboxLabel={modalCheckboxLabel}
            modalCheckboxValue={modalCheckboxValue}
            hideCheckbox={isAnaSharedAndAlreadyConnected}
            hideConfirm={isAnaSharedAndAlreadyConnected}
            onChange={handleCheckboxChange}
            dataTestId="connect-content-modal"
          />
        </Modal>
      </div>

      <div className="scrollable">
        {options.map((content) =>
          connectedContentType === 'analyses' ? (
            <div
              data-testid="option-container"
              className={`${
                currentlySelectedTags[content.title]
                  ? `option-container__active${
                      isConnected(content) ? '_connection' : ''
                    }`
                  : parentContentType !== Constants.ContentTypes.Analysis &&
                    isConnected(content)
                  ? 'option_container_right_connections'
                  : 'option-container'
              }`}
              key={content.contentId}
            >
              <Checkbox
                label={truncate(content.title)}
                id={`${content.contentId}`}
                dataTestId={`${content.contentId}`}
                checked={currentlySelectedTags[content.title] === true}
                onChange={() => {
                  setCurrentNarrative(content);
                  updateCheckedTags(content);
                }}
                onBlur={blurHandler}
              />
              {parentContentType !== Constants.ContentTypes.Analysis && (
                <div
                  className="right-connected-content"
                  data-testid="right-connected-content"
                >
                  {handleConnectedContent(content)}
                </div>
              )}
            </div>
          ) : (
            <div
              key={content.contentId}
              className="kfa-option-container"
              data-testid="kfa-option-container"
            >
              <button
                className="kfa-dropdown-button"
                onClick={() => {
                  updateKeyFocusAreas(content);
                }}
                type="button"
              >
                {truncate(content.title)}
              </button>
            </div>
          )
        )}
      </div>

      {connectedContentType === 'analyses' && (
        <div className="multi-select-action-button-container">
          <Button
            id="connected-content-action-button"
            data-testid="connected-content-action-button"
            onClick={handleOnSubmit}
            onBlur={blurHandler}
            type="button"
            onKeyDown={handleEscapeKey}
            disabled={!tagsChanged}
          >
            {t('btnApply')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ConnectedContentRow;
